import React, { useEffect, useState } from "react";
import * as notify from "shared/notify";
import { dialogConfirm } from "shared/dialog";
import { DialogRejectReason } from "components/task-nav/components/dialog-reject";
import { useColumnSize } from "shared/hooks/use-column-size";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedo } from "@fortawesome/free-solid-svg-icons";
import { useSendNotes } from "shared/hooks/api/use-send-notes";
import { useRejectTask } from "shared/hooks/api/use-reject-task";
import { SourcePage, TaskType } from "shared/taskTypeMap";
import { SECTION_MAP } from "components/task-list/lib/constants";
import { useCoderRejectValidation } from "components/coding/coding-qa/hooks/use-coder-reject-validation";
import { getTaskByKey } from "components/task-list/lib/taskData";
import { Tooltip } from "components/tooltip/tooltip";
import { useViewOnlyPermissions } from "shared/hooks/use-view-only-permissions";

import moment from "moment";
import { CODING_QA_MOMENT_LONG_DATE_FORMAT } from "shared/constants";

window.refreshTimer = null;

export const CoderRolesHeader = ({
    col,
    role,
    onColumnWidthChange,
    toggleModalSelect,
    hideSelect,
    isCompleted,
    task,
    setHasRedo,
    refetchCoderRoles,
    isSalesSample,
    isBrandVerified,
    styleOverride,
}) => {
    const { colRef, resizerRef, width, initialize } = useColumnSize();
    const { mutate: validateReject } = useCoderRejectValidation();
    const { mutate: sendNotes } = useSendNotes();
    const { mutate: rejectTask } = useRejectTask();
    const [displayRedo, setDisplayRedo] = useState(false);
    const [updateRedoInfo, setUpdateRedoInfo] = useState(false);
    const [coderInfo, setCoderInfo] = useState({
        taskUpdatedOn: null,
        taskUpdatedBy: null,
    });
    const sourcePage = SourcePage[task.taskType];
    const isVORejectTask = useViewOnlyPermissions("RejectTask", task.sourcePage);

    useEffect(() => {
        if (role.taskState === "redo") {
            setDisplayRedo(true);
            fetchCoderInfo();
        } else {
            setDisplayRedo(false);
        }
    }, [role.taskState]);

    useEffect(() => {
        if (updateRedoInfo && refetchCoderRoles) {
            refetchCoderRoles();
        }
    }, [updateRedoInfo, refetchCoderRoles]);

    useEffect(() => {
        if (width !== 0) {
            onColumnWidthChange(col, width);
        }
    }, [col, onColumnWidthChange, width]);

    const fetchCoderInfo = async () => {
        await getTaskByKey(role.taskKey).then((data) => {
            return setCoderInfo({
                taskUpdatedOn: data.taskUpdatedOn,
                taskUpdatedBy: data.taskUpdatedBy,
            });
        });
    };

    const SelectReference = ({ visible }) => {
        return (
            visible && (
                <div className="button-group -stacked">
                    <button
                        className="button"
                        onClick={toggleModalSelect}
                    >
                        Select Reference
                    </button>
                </div>
            )
        );
    };

    const checkValidation = async (response, taskKey, prompt) => {
        if (response.isValid) {
            rejectCodingTask(taskKey, prompt);
        } else {
            const confirm = await notify.confirm(response.overridableErrors && response.overridableErrors[0]);

            if (!confirm) {
                return;
            }

            validateReject(
                { taskKey: taskKey, isRejection: true, override: true },
                {
                    onSuccess: (data) => {
                        if (data.isValid) {
                            rejectCodingTask(taskKey, prompt);
                        }
                    },
                }
            );
        }
    };

    const rejectCodingTask = (taskKey, prompt) => {
        rejectTask(
            { taskKey: taskKey, override: null, sourcePage: sourcePage, action: "reject" },
            {
                onSuccess: () => {
                    saveNotes(prompt);
                    setHasRedo(true);
                    setUpdateRedoInfo(true);
                },
            }
        );
    };

    const saveNotes = (prompt) => {
        const noteTaskType = SECTION_MAP["coding"].noteKey;

        return sendNotes({
            items: [
                {
                    noteText: prompt.reason,
                    noteType: "Product",
                    shipmentKey: task.context.shipmentKey,
                    shipmentProductKey: task.context.shipmentProductKey,
                    taskType: noteTaskType,
                    sourcePage: sourcePage,
                },
            ],
        });
    };

    const onRedoClickHandler = async (taskKey) => {
        if (!task.context.shipmentKey || !task.context.shipmentProductKey) {
            notify.error("Missing ShipmentKey and/or ShipmentProductKey.");
        }

        const prompt = await dialogConfirm({
            Component: DialogRejectReason,
            message: `
            Are you sure you want to reject ${role.taskLabel} ${role.label} and request a redo? This action cannot be undone.
            `,
            cssClass: "-reject-modal",
        });

        if (prompt.length <= 0) {
            notify.error("A reason must be provided to redo a task");

            return;
        }

        if (!prompt.reject) {
            return;
        }

        validateReject(
            { taskKey: taskKey, isRejection: true },
            {
                onSuccess: (response) => checkValidation(response, taskKey, prompt),
            }
        );
    };

    const redoDisplay = () => {
        if (task.taskType === TaskType.BRAND_VERIFIED_QA) {
            return;
        }

        if (displayRedo) {
            let updatedDetailsAvailable = !!coderInfo.taskUpdatedOn && !!coderInfo.taskUpdatedBy;

            return (
                <div className="txt-error m-t-4">
                    <div className="txt-bold m-b-4">
                        Rejected back to {role.taskLabel} {role.label}
                    </div>
                    {updatedDetailsAvailable && (
                        <div>
                            <em>
                                <small>
                                    {moment.utc(coderInfo.taskUpdatedOn).format(CODING_QA_MOMENT_LONG_DATE_FORMAT)} by {coderInfo.taskUpdatedBy}
                                </small>
                            </em>
                        </div>
                    )}
                </div>
            );
        } else {
            const tooltip = task.context.isPreDualCoding ? "This product was completed prior to Dual Coding" : "";

            return (
                <div className="button-group -stacked">
                    <Tooltip
                        text={tooltip}
                        classes={task.context.isPreDualCoding ? "" : "hidden"}
                    >
                        <button
                            className="button -primary -whitespace"
                            onClick={() => onRedoClickHandler(role.taskKey, role.taskType)}
                            disabled={task.context.isPreDualCoding || isVORejectTask || isSalesSample}
                        >
                            <FontAwesomeIcon
                                icon={faRedo}
                                className="m-r-4"
                            />
                            Reject and Request Redo
                        </button>
                    </Tooltip>
                </div>
            );
        }
    };

    return (
        <div
            ref={colRef}
            onMouseDown={initialize}
            className={styleOverride}
        >
            <div
                ref={resizerRef}
                className="border-resizer"
            />
            <h4>
                {isBrandVerified ? "Brand" : ""} {role.taskLabel} {role.label}
            </h4>
            {(role.type !== "qa" || task.taskType !== TaskType.BRAND_VERIFIED_QA) && (
                <>
                    <p>Last updated: {moment.utc(role.lastUpdated).format(CODING_QA_MOMENT_LONG_DATE_FORMAT)}</p>
                    <p>Last updated by: {role.updatedBy}</p>
                    {!isCompleted && role.type !== "qa" && redoDisplay()}
                </>
            )}

            <SelectReference visible={!hideSelect && role.type === "qa"} />
        </div>
    );
};
